/**
 * Application Configuration
 * 
 * Central configuration file for the entire application
 */

const API_BASE_URL_FALLBACK = 'http://localhost:3000/api/v1';

function resolvePublicApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/v1`;
  }
  return (
    process.env.NEXT_PUBLIC_API_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    API_BASE_URL_FALLBACK
  );
}

function resolvePublicWebsitesApiUrl(): string {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/websites-api`;
  }
  return (
    process.env.NEXT_PUBLIC_WEBSITES_API_URL ||
    process.env.WEBSITES_API_INTERNAL ||
    'http://127.0.0.1:3163'
  );
}

export const appConfig = {
  // API Configuration (single source for base URL)
  api: {
    baseUrl: resolvePublicApiBaseUrl(),
    timeout: 30000, // 30 seconds
  },

  // Association: resolution (defaultId, override, domain) lives in @/config/association.
  // Use getAssociationId() (client) or getServerAssociationIdForRequest() (server).

  // Pagination
  pagination: {
    defaultPageSize: 12,
    maxPageSize: 100,
  },

  // Locale Configuration
  locale: {
    default: 'ar',
    supported: ['ar', 'en'],
  },

  // Feature Flags
  features: {
    enableAuthentication: true,
    enablePayments: true,
    enableGiftDonations: true,
    enableProducts: true,
    enableVolunteer: true,
    // Project comments are not exposed in the public API as of now.
    // Keep UI hidden unless explicitly enabled.
    enableProjectComments: true,
    // When true, ProjectCard uses pre-loaded shares from the homepage bundle
    // instead of making individual /api/projects/:id/shares calls.
    // Set to false for instant rollback without a deploy.
    enableBundleShares: true,
  },

  // Cart Configuration
  cart: {
    storageKey: 'jood-cart',
    maxItems: 50,
  },

  // Image Configuration
  images: {
    placeholder: '/placeholder-project.jpg',
    cdnUrl: process.env.NEXT_PUBLIC_CDN_URL || '',
  },

  // Social Media (can be customized per association)
  social: {
    facebook: 'https://facebook.com',
    twitter: 'https://twitter.com',
    instagram: 'https://instagram.com',
    youtube: 'https://youtube.com',
  },

  // Contact Information (default, can be overridden by association data)
  contact: {
    phone: '0520000002',
    email: 'salah2@sondoq.tech',
    address: 'طريق الملك عبدالله - الرياض',
  },
} as const;

/** API base URL – use this instead of reading env directly. */
export const apiBaseUrl = appConfig.api.baseUrl;

/**
 * Internal (server-side only) Donations ERP URL for commerce.
 * Set API_URL_INTERNAL in .env (e.g. http://127.0.0.1:3159).
 */
export const apiInternalUrl: string =
  (typeof window === "undefined" ? process.env.API_URL_INTERNAL : undefined) ||
  apiBaseUrl;

/** Legacy BFF (MSSQL) for routes not yet on ERP/Websites. */
export const legacyApiInternalUrl: string =
  (typeof window === "undefined" ? process.env.LEGACY_API_URL_INTERNAL : undefined) ||
  "http://127.0.0.1:3036/api/v1";

/** Websites CMS API (builder pages, menus, templates). */
export const websitesApiInternalUrl: string =
  (typeof window === 'undefined' ? process.env.WEBSITES_API_INTERNAL : undefined) ||
  resolvePublicWebsitesApiUrl();

// Type-safe config getter
export function getConfig<T extends keyof typeof appConfig>(key: T): typeof appConfig[T] {
  return appConfig[key];
}

// Environment helper
export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';
export const isServer = typeof window === 'undefined';
export const isClient = typeof window !== 'undefined';


